// @ts-nocheck
import { motion } from "framer-motion";
import {
  Calendar,
  Flame,
  GamepadIcon,
  Shield,
  Star,
  Trophy,
  Users,
  Zap,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import Avatar from "@/components/others/Avatar";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface TeamCardProps {
  team: any;
  gamesFilter: any[];
}

const TeamCard = ({ team, gamesFilter = [] }: TeamCardProps) => {
  const navigate = useNavigate();

  // Get profile and banner images
  const profilePicture =
    team.files && team.files.length > 0
      ? team.files.find((file) => file.business_type === "TEAM_PROFILE_PHOTO")
          ?.url
      : null;
  const profileBanner =
    team.files && team.files.length > 0
      ? team.files.find((file) => file.business_type === "TEAM_BANNER_PHOTO")
          ?.url
      : null;

  // Format creation date if available
  const formattedDate = team.entity.created_at 
    ? format(new Date(team.entity.created_at), "dd MMM yyyy", { locale: ptBR })
    : null;

  // Get performance stats if available
  const hasPerformance = team.entity.performance && 
    (team.entity.performance.wins > 0 || team.entity.performance.losses > 0);

  // Get game name
  const gameName = team.entity.game_id && gamesFilter.length > 0
    ? gamesFilter.find(game => game.id === team.entity.game_id)?.name
    : null;

  return (
    <motion.div
      className="bg-gradient-to-b from-gray-800/60 to-gray-900/60 rounded-xl overflow-hidden border border-gray-700/50 hover:border-green-500/30 transition-all duration-300 shadow-lg cursor-pointer"
      whileHover={{ y: -5, scale: 1.02 }}
      transition={{ type: "spring", stiffness: 300 }}
      onClick={() => navigate(`/my/teams/${team.entity.team_tag}`)}
    >
      {/* Status Badge - Recruiting */}
      {team.entity.is_recruiting && (
        <Badge className="absolute top-2 left-2 z-20 bg-blue-500/80 text-white font-medium px-2 py-1">
          Recrutando
        </Badge>
      )}

      {/* Banner Image */}
      <div className="relative h-40 w-full overflow-hidden border-b border-gray-800/70">
        {profileBanner ? (
          <img
            src={profileBanner}
            alt="Banner"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-r from-gray-800 to-gray-700 flex items-center justify-center">
            <GamepadIcon size={40} className="text-gray-600" />
          </div>
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 to-transparent"></div>

        {/* Team Tag */}
        {team.entity.team_tag && (
          <Badge className="absolute top-2 right-2 bg-green-600/80 text-white font-medium px-2 py-1">
            {team.entity.team_tag}
          </Badge>
        )}
      </div>

      <div className="px-6 pb-6 -mt-12 relative">
        {/* Profile Picture */}
        <div className="relative z-10 mb-4">
          <motion.div whileHover={{ scale: 1.05 }} transition={{ type: "spring", stiffness: 400 }}>
            <Avatar
              sizeClass="h-24 w-24 ring-4 ring-gray-800 shadow-lg"
              children={
                <span className="text-[3rem] font-bold">
                  {team?.entity.name.charAt(0)}
                </span>
              }
              src={profilePicture}
            />
          </motion.div>
        </div>

        {/* Team Info */}
        <div className="flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xl font-bold text-white bg-gradient-to-r from-green-400 to-blue-500 bg-clip-text text-transparent">
              {team.entity.name}
            </h3>
          </div>

          {team.entity.description && (
            <p className="text-gray-300 text-sm mb-4 line-clamp-2 italic border-l-2 border-green-500/30 pl-2">
              {team.entity.description}
            </p>
          )}

          {/* Team Stats Section */}
          <div className="bg-gray-800/50 rounded-lg p-3 mb-4 border border-gray-700/50 backdrop-blur-sm">
            {/* Game - Moved to top for prominence */}
            {gameName && (
              <div className="flex items-center gap-2 mb-3 bg-yellow-500/10 p-2 rounded-md border border-yellow-500/20">
                <GamepadIcon size={16} className="text-yellow-400" />
                <p className="text-sm text-gray-300">
                  Jogo:{" "}
                  <span className="font-medium text-yellow-400">
                    {gameName}
                  </span>
                </p>
              </div>
            )}

            {/* Creation Date */}
            {formattedDate && (
              <div className="flex items-center gap-2 mb-2">
                <Calendar size={16} className="text-blue-400" />
                <p className="text-sm text-gray-300">
                  Criado em:{" "}
                  <span className="font-medium text-blue-400">
                    {formattedDate}
                  </span>
                </p>
              </div>
            )}

            {/* Team Members */}
            <div className="flex items-center gap-2 mb-2">
              <Users size={16} className="text-green-400" />
              <p className="text-sm text-gray-300">
                Membros:{" "}
                <span className="font-medium text-green-400">
                  {team.entity.team_members_count || 0}
                </span>
              </p>
            </div>

            {/* Owner */}
            {team.entity.owner && (
              <div className="flex items-center gap-2 mb-2">
                <Shield size={16} className="text-purple-400" />
                <p className="text-sm text-gray-300">
                  Proprietário:{" "}
                  <span className="font-medium text-purple-400">
                    {team.entity.owner.name}
                  </span>
                </p>
              </div>
            )}

            {/* Performance Stats */}
            {hasPerformance && (
              <div className="mt-2 pt-2 border-t border-gray-700/50">
                <div className="flex items-center gap-1 mb-1">
                  <Trophy size={14} className="text-amber-400" />
                  <p className="text-xs text-amber-400 font-medium">Desempenho</p>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <Flame size={14} className="text-green-400" />
                    <span className="text-xs text-gray-300">
                      Vitórias: <span className="text-green-400 font-medium">{team.entity.performance.wins || 0}</span>
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Zap size={14} className="text-red-400" />
                    <span className="text-xs text-gray-300">
                      Derrotas: <span className="text-red-400 font-medium">{team.entity.performance.losses || 0}</span>
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Team Members Avatars */}
          {team.entity.team_members && team.entity.team_members.length > 0 ? (
            <div className="mb-4 bg-gray-800/30 rounded-lg p-3 border border-gray-700/30">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-gray-300 flex items-center gap-1">
                  <Star size={12} className="text-green-400" />
                  <span className="font-medium">Membros do time</span>
                </p>
                {team.entity.team_members.length > 5 && (
                  <Badge className="bg-green-500/20 text-green-400 text-xs px-2">
                    {team.entity.team_members.length} total
                  </Badge>
                )}
              </div>

              <div className="flex -space-x-2 overflow-hidden">
                {team.entity.team_members.slice(0, 5).map((member, index) => (
                  <motion.div 
                    key={index}
                    whileHover={{ y: -3 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <Avatar
                      sizeClass="h-8 w-8"
                      src={member.avatar || null}
                      children={
                        <span className="text-[0.8rem] font-bold">
                          {member.entity?.user_name?.charAt(0) || "?"}
                        </span>
                      }
                    />
                  </motion.div>
                ))}
                {team.entity.team_members.length > 5 && (
                  <motion.div 
                    whileHover={{ y: -3 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <div className="h-8 w-8 rounded-full bg-gradient-to-br from-green-600/40 to-blue-600/40 flex items-center justify-center text-xs font-medium text-white">
                      +{team.entity.team_members.length - 5}
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          ) : (
           null
          )}

        </div>
      </div>
    </motion.div>
  );
};

export default TeamCard;
